/**
 * Shopify Credentials Modal
 *
 * OAuth-based connection flow:
 *  1. Merchant enters their shop domain + app client_id + client_secret
 *  2. "Connect with Shopify" calls /api/shopify/oauth/start → gets authUrl
 *  3. Browser redirects to Shopify for merchant approval
 *  4. Shopify redirects back to /oauth/shopify/callback which saves the token
 *  5. On return, the modal shows the connected state
 *
 * Per-tenant client_id/secret supports custom-distribution apps where each
 * customer has their own Shopify app credentials.
 */

import React, { useState, useEffect } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Button, Text, BlockStack, Box } from '@shopify/polaris';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function getAuthHeaders() {
  const token = localStorage.getItem('tenantAccessToken') || localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

export function ShopifyCredentialsModal({ open, onClose, onSave, config = {} }) {
  const [shop, setShop] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ ok: null, message: '' });

  const isConnected = !!(config.accessTokenEnc);

  // Populate fields from existing config when modal opens
  useEffect(() => {
    if (open) {
      setApiVersion(config.apiVersion || '2025-10');
      setShop(config.domain || config.store || '');
      setClientId(config.clientId || '');
      // Show masked placeholder if secret is already stored
      setClientSecret(config.clientSecretEnc ? '****' : '');
    }
  }, [open, config.apiVersion, config.clientId, config.clientSecretEnc, config.domain, config.store]);

  // Check for OAuth result params on mount — runs once regardless of modal state.
  // After Shopify redirects back, the modal is closed (full page navigation occurred),
  // so we cannot gate this check on `open`.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('shopify_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      // Notify parent to refresh credentials and re-open modal with connected state
      if (onSave) onSave();
    } else if (params.get('shopify_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      setStatus({ ok: false, message: `Connection failed: ${params.get('shopify_error')}` });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setShop('');
      setClientId('');
      setClientSecret('');
      setConnecting(false);
      setSaving(false);
      setStatus({ ok: null, message: '' });
    }
  }, [open]);

  const handleConnect = async () => {
    const domain = shop.trim();
    if (!domain) {
      setStatus({ ok: false, message: 'Please enter your shop domain' });
      return;
    }
    setConnecting(true);
    setStatus({ ok: null, message: '' });
    try {
      const body = { shop: domain };
      // Include per-tenant credentials if provided; backend falls back to env if omitted
      if (clientId.trim()) body.clientId = clientId.trim();
      if (clientSecret.trim() && clientSecret !== '****') body.clientSecret = clientSecret.trim();
      const resp = await fetch(`${API_BASE_URL}/api/shopify/oauth/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to start OAuth');
      }
      // Redirect merchant to Shopify for approval
      window.location.href = data.authUrl;
    } catch (err) {
      setStatus({ ok: false, message: err.message || 'Failed to start connection' });
      setConnecting(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setStatus({ ok: null, message: '' });
    try {
      const resp = await fetch(`${API_BASE_URL}/api/shopify/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          domain: shop.trim() || config.domain || config.store,
          clientId: clientId.trim() || undefined,
          clientSecret: clientSecret.trim() && clientSecret !== '****' ? clientSecret.trim() : undefined,
          apiVersion: apiVersion.trim() || '2025-10'
        })
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to save settings');
      }
      setStatus({ ok: true, message: 'Settings saved' });
      if (onSave) onSave();
    } catch (err) {
      setStatus({ ok: false, message: err.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Connect Shopify Store"
      primaryAction={{
        content: isConnected ? 'Reconnect' : 'Connect with Shopify',
        onAction: handleConnect,
        disabled: connecting || !shop.trim(),
        loading: connecting
      }}
      secondaryActions={[
        { content: 'Save Settings', onAction: handleSaveSettings, loading: saving, disabled: saving },
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {isConnected && (
            <InlineStack gap="200" align="center">
              <Badge tone="success">Connected</Badge>
              <Badge tone={config.webhookHmacConfigured ? 'success' : 'attention'}>
                {config.webhookHmacConfigured ? 'Webhook HMAC ready' : 'Webhook HMAC missing'}
              </Badge>
              <Text as="span" tone="subdued">{config.domain}</Text>
            </InlineStack>
          )}

          <TextField
            label="Shop Domain"
            value={shop}
            onChange={setShop}
            placeholder="my-store or my-store.myshopify.com"
            helpText={isConnected ? 'Enter your shop domain to reconnect with a new token' : 'Enter your Shopify store name or full domain'}
            autoComplete="off"
          />

          <TextField
            label="Client ID"
            value={clientId}
            onChange={setClientId}
            placeholder="Shopify app Client ID (API key)"
            helpText="From Shopify Partner Dashboard → App setup. Leave blank to use the server default."
            autoComplete="off"
          />

          <TextField
            label="Client Secret"
            value={clientSecret}
            onChange={setClientSecret}
            placeholder="Shopify app Client Secret (API secret key)"
            helpText={config.clientSecretEnc ? 'Stored — used for OAuth and Shopify webhook HMAC validation. Enter a new value to replace, or leave as **** to keep existing.' : 'From Shopify Partner Dashboard → App setup. This is also required for Shopify webhook HMAC validation.'}
            type="password"
            autoComplete="off"
          />

          <TextField
            label="API Version"
            value={apiVersion}
            onChange={setApiVersion}
            placeholder="2025-10"
            helpText="Shopify Admin API version used for all GraphQL and REST calls (e.g. 2025-10)"
            autoComplete="off"
          />

          {status.message && (
            <InlineStack gap="200">
              <Badge tone={status.ok ? 'success' : status.ok === false ? 'critical' : 'info'}>
                {status.ok ? '✓ Saved' : status.ok === false ? '✗ Error' : 'Info'}
              </Badge>
              <Text as="span">{status.message}</Text>
            </InlineStack>
          )}

          <Box>
            <Text as="p" tone="subdued" variant="bodySm">
              Clicking "Connect with Shopify" will redirect you to Shopify for approval.
              Use "Save Settings" to update the API version without reconnecting.
              Client ID and Secret are required for custom-distribution apps; the Client Secret is also used to validate Shopify webhook signatures.
            </Text>
          </Box>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
